import React, { useState, useEffect } from 'react';
import { X, User, MapPin, Calendar, Clock, CheckCircle, FileText, ShieldCheck, File } from '@phosphor-icons/react';
import ExpansionPanel from '../../../../shared/components/ui/ExpansionPanel.jsx';
import ValidationRequestModal from './ValidationRequestModal';
import { requestTaskValidation } from '../../../../services/technicianService.js';
import { fetchTaskDocuments, fetchTaskDocumentBlob } from '../../../../services/tasks/taskDocumentsService.js';
import { DownloadSimple } from '@phosphor-icons/react';

/**
 * Modal para ver detalles completos de una tarea histórica
 * @param {Object} task - Tarea a mostrar (incluye formResponses del endpoint /my-history)
 * @param {boolean} isOpen - Si el modal está abierto
 * @param {Function} onClose - Callback para cerrar
 */
function TaskDetailModal({ task, isOpen, onClose }) {
    // Usar formResponses directamente del objeto task (viene de /my-history)
    const formResponses = task?.formResponses || [];

    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
    const [isValidationLoading, setIsValidationLoading] = useState(false);
    const [localValidation, setLocalValidation] = useState(task?.validationRequest || null);
    const [documents, setDocuments] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [docsDownloading, setDocsDownloading] = useState(null);

    useEffect(() => {
        setLocalValidation(task?.validationRequest || null);
    }, [task]);

    useEffect(() => {
        if (!isOpen || !task?.id) return;
        let cancelled = false;
        setDocsLoading(true);
        setDocuments([]);
        fetchTaskDocuments(task.id)
            .then((docs) => { if (!cancelled) setDocuments(docs.filter((d) => d.visibleToTechnician)); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setDocsLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, task?.id]);

    const formatDocSize = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleDocDownload = async (doc) => {
        setDocsDownloading(doc.id);
        try {
            const { blob, fileName } = await fetchTaskDocumentBlob(task.id, doc);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(err.message || 'No se pudo descargar el documento.');
        } finally {
            setDocsDownloading(null);
        }
    };

    /**
     * Enviar solicitud de validación al backend
     * @param {string} reasonTechnician - Motivo ingresado por el técnico
     */
    const handleRequestValidation = async (reasonTechnician) => {
        setIsValidationLoading(true);
        try {
            await requestTaskValidation(task.id, reasonTechnician);
            setIsValidationModalOpen(false);
            setLocalValidation({
                status: 'pendiente',
                reason: reasonTechnician,
                requestedAt: new Date().toISOString()
            });
        } catch (err) {
            console.error('Error al solicitar validación:', err);
        } finally {
            setIsValidationLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'finalizada': { bg: 'bg-st-finalizada/12', text: 'text-st-finalizada', border: 'border-st-finalizada/30', label: 'Finalizada' },
            'cancelada': { bg: 'bg-st-cancelada/12', text: 'text-st-cancelada', border: 'border-st-cancelada/30', label: 'Cancelada' },
            'en_camino': { bg: 'bg-st-en-camino/12', text: 'text-st-en-camino', border: 'border-st-en-camino/30', label: 'En Camino' },
            'iniciada': { bg: 'bg-st-en-camino/12', text: 'text-st-en-camino', border: 'border-st-en-camino/30', label: 'Iniciada' },
            'pendiente': { bg: 'bg-st-pendiente/12', text: 'text-st-pendiente', border: 'border-st-pendiente/30', label: 'Pendiente' },
            'relevada': { bg: 'bg-st-relevada/12', text: 'text-st-relevada', border: 'border-st-relevada/30', label: 'Relevada' },
        };

        const style = statusMap[status] || { bg: 'bg-muted-deep/12', text: 'text-muted-deep', border: 'border-line', label: status };

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${style.bg} ${style.text} ${style.border}`}>
                {style.label}
            </span>
        );
    };

    const renderFormData = (formData) => {
        if (!formData || Object.keys(formData).length === 0) {
            return <p className="text-content-muted text-sm">Sin datos de formulario</p>;
        }

        return (
            <div className="space-y-3">
                {Object.entries(formData).map(([key, value]) => (
                    <div key={key} className="flex flex-col">
                        <span className="text-xs text-content-muted capitalize tracking-wide">{key}</span>
                        <span className="text-content text-sm mt-1">
                            {Array.isArray(value)
                                ? value.length === 0
                                    ? '-'
                                    : value.map((v, i) => <span key={i} className="block">{v.originalname || JSON.stringify(v)}</span>)
                                : typeof value === 'object'
                                    ? JSON.stringify(value, null, 2)
                                    : typeof value === 'boolean'
                                        ? value ? "Si" : "No"
                                        : value || '-'
                            }
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    if (!isOpen || !task) return null;

    return (
        <>
            <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[9999]">
                <div className="bg-surface rounded-xl shadow-xl w-[95vw] max-w-3xl max-h-[90vh] flex flex-col border border-line">
                {/* Header */}
                <div className="flex justify-between items-start p-5 border-b border-line">
                    <div className="flex-1 pr-4">
                        <h3 className="text-xl font-bold text-content">{task.title}</h3>
                        <p className="text-content-muted text-sm mt-1">{task.taskType?.name || task.taskTypeId || 'Tarea'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {getStatusBadge(task.status)}
                        <button
                            onClick={onClose}
                            className="text-content-muted hover:text-content transition-colors p-1"
                        >
                            <X size={24} weight="bold" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* Información del cliente */}
                    <div className="bg-surface-2/50 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-content-muted capitalize tracking-wide flex items-center gap-2">
                            <User size={16} />
                            Información del Cliente
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-xs text-content-muted">Nombre</span>
                                <p className="text-content">{task.client?.name || 'N/A'}</p>
                            </div>
                            <div>
                                <span className="text-xs text-content-muted">CUIL</span>
                                <p className="text-content">{task.client?.cuil || 'N/A'}</p>
                            </div>
                            {task.client?.address && (
                                <div className="md:col-span-2">
                                    <span className="text-xs text-content-muted flex items-center gap-1">
                                        <MapPin size={12} />
                                        Dirección
                                    </span>
                                    <p className="text-content">{task.client.address}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Fechas */}
                    <div className="bg-surface-2/50 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-content-muted capitalize tracking-wide flex items-center gap-2">
                            <Calendar size={16} />
                            Fechas
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-xs text-content-muted">Fecha Programada</span>
                                <p className="text-content">{formatDateTime(task.scheduledDate)}</p>
                            </div>
                            {task.startedAt && (
                                <div>
                                    <span className="text-xs text-content-muted">Fecha Inicio</span>
                                    <p className="text-content">{formatDateTime(task.startedAt)}</p>
                                </div>
                            )}
                            {task.finishedAt && (
                                <div>
                                    <span className="text-xs text-content-muted flex items-center gap-1">
                                        <CheckCircle size={12} className="text-success" />
                                        Fecha Finalización
                                    </span>
                                    <p className="text-content">{formatDateTime(task.finishedAt)}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Documentos */}
                    <div className="bg-surface-2/50 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-content-muted capitalize tracking-wide flex items-center gap-2">
                            <File size={16} />
                            Documentos
                        </h4>
                        {docsLoading && (
                            <p className="text-xs text-content-muted">Cargando documentos...</p>
                        )}
                        {!docsLoading && documents.length === 0 && (
                            <p className="text-xs text-muted-deep">No hay documentos disponibles.</p>
                        )}
                        {!docsLoading && documents.length > 0 && (
                            <ul className="divide-y divide-line/50 max-h-60 overflow-y-auto">
                                {documents.map((doc) => (
                                    <li key={doc.id} className="flex items-center gap-3 py-2">
                                        {doc.extension && (
                                            <span className="flex-shrink-0 text-xs font-bold text-content-muted bg-surface-2/60 px-1.5 py-0.5 rounded uppercase">
                                                {doc.extension}
                                            </span>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-content truncate">{doc.name}</p>
                                            <p className="text-xs text-content-muted">
                                                {[formatDocSize(doc.size), doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('es-AR') : ''].filter(Boolean).join(' · ')}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleDocDownload(doc)}
                                            disabled={docsDownloading === doc.id}
                                            className="flex-shrink-0 hover:bg-surface-2 disabled:opacity-50 p-1.5 rounded-lg transition-colors"
                                        >
                                            {docsDownloading === doc.id ? (
                                                <svg className="animate-spin text-content" width={18} height={18} viewBox="0 0 24 24" fill="none">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                                </svg>
                                            ) : (
                                                <DownloadSimple size={18} className="text-content" />
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Card de validación solicitada */}
                    {localValidation && (() => {
                        const isValidate = localValidation.status === 'validada';
                        const theme = isValidate
                            ? { bg: 'bg-success/12', border: 'border-success/30', text: 'text-success', accent: 'text-success', borderTop: 'border-success/30', display: 'Validado' }
                            : { bg: 'bg-warning/12', border: 'border-warning/30', text: 'text-warning', accent: 'text-warning', borderTop: 'border-warning/30', display: 'Pendiente' };

                        return (
                            <div className={`${theme.bg} rounded-lg p-4 border ${theme.border} space-y-2`}>
                                <h4 className={`text-sm font-semibold ${theme.text} flex items-center gap-2`}>
                                    <ShieldCheck size={16} />
                                    Validación Solicitada
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <span className="text-xs text-content-muted">Estado</span>
                                        <p className={`${theme.text} font-medium`}>{theme.display}</p>
                                    </div>
                                    {localValidation.requestedAt && (
                                        <div>
                                            <span className="text-xs text-content-muted">Solicitada el</span>
                                            <p className="text-content text-sm">{formatDateTime(localValidation.requestedAt)}</p>
                                        </div>
                                    )}
                                    {localValidation.reason && (
                                        <div className="md:col-span-2">
                                            <span className="text-xs text-content-muted">Motivo</span>
                                            <p className="text-content text-sm mt-0.5">{localValidation.reason}</p>
                                        </div>
                                    )}
                                    {(isValidate || task?.validateReason?.reasonAdmin) && (
                                        <div className={`md:col-span-2 mt-1 pt-2 border-t ${theme.borderTop}`}>
                                            <span className={`text-xs ${theme.accent} font-medium tracking-wide`}>Devolución</span>
                                            <p className="text-content-muted text-sm italic mt-0.5 whitespace-pre-wrap">{task?.validateReason?.reasonAdmin || 'Sin devoluciones'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Formularios */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-content-muted capitalize tracking-wide flex items-center gap-2">
                            <FileText size={16} />
                            Respuestas de Formularios ({formResponses.length})
                        </h4>

                        {formResponses.length === 0 ? (
                            <div className="bg-surface-2/30 rounded-lg p-4 text-center text-content-muted">
                                No hay formularios asociados a esta tarea
                            </div>
                        ) : (
                            formResponses.map((response, index) => (
                                <ExpansionPanel
                                    key={response.id || index}
                                    title={`${response.form.name || 'Formulario'} - ${response.form.type || 'general'}`}
                                    icon={<FileText size={16} />}
                                    defaultOpen={index === 0}
                                >
                                    <div className="space-y-4">
                                        {/* Metadata del formulario */}
                                        <div className="flex items-center gap-4 text-xs text-content-muted">
                                            <span className="flex items-center gap-1">
                                                <Clock size={12} />
                                                {formatDateTime(response.createdAt)}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full ${response.status === 'completado'
                                                ? 'bg-success/12 text-success'
                                                : 'bg-surface-2 text-content-muted'
                                                }`}>
                                                {response.status || 'completado'}
                                            </span>
                                        </div>

                                        {/* Datos del formulario */}
                                        {response.cancellationData !== null ? renderFormData({...response.formData, ...response.cancellationData})
                                        : renderFormData(response.formData)}
                                    </div>
                                </ExpansionPanel>
                            ))
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className={`p-4 border-t border-line flex items-center ${(!localValidation && (task?.status === 'finalizada' || task?.status === 'cancelada')) ? 'justify-between' : 'justify-end'}`}>
                    {!localValidation && (task?.status === 'finalizada' || task?.status === 'cancelada') && (
                        <button
                            onClick={() => setIsValidationModalOpen(true)}
                            className="px-4 py-2 bg-amber hover:bg-amber-hover text-on-amber rounded-lg transition-colors font-medium"
                        >
                            Solicitar validación
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-surface-2 hover:bg-line text-content rounded-lg transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>

            <ValidationRequestModal
                isOpen={isValidationModalOpen}
                onClose={() => setIsValidationModalOpen(false)}
                onConfirm={handleRequestValidation}
                isLoading={isValidationLoading}
            />
        </>
    );

}

export default TaskDetailModal;

